import { useState } from "react";

import { CreateInvoice } from "../../../../types/types";
import { useMutateInvoices } from "../hooks/useMutateInvoices";

import { Box, Button } from "@chakra-ui/react";
import { toaster } from "../../../../components/ui/toaster";

import { Plus } from "lucide-react";
import { useMerchant } from "../../../../hooks/useMerchant";
import { useEditCreateInvoice } from "../hooks/useEditCreateInvoice";
import AddInvoiceModal from "./addInvoiceModal";
import UploadFileModal from "./UploadFileModal";

export type InvoiceModal = "upload" | "add";

type Props = {
  refetch: () => void;
  modal: InvoiceModal | null;
  setModal: (modal: InvoiceModal | null) => void;
};

const Buttons = ({ refetch, modal, setModal }: Props): JSX.Element => {
  const [isAddingInvoice, setIsAddingInvoice] = useState(false);

  const { merchant } = useMerchant();

  const { invoiceBatch, getUploadUrl, resetBatch } = useMutateInvoices(refetch);

  /////////////////////////////// MANUAL CREATE //////////////////////////////////
  async function handleCreateInvoice(
    invoiceData: CreateInvoice
  ): Promise<void> {
    setIsAddingInvoice(true);

    try {
      await createInvoiceManually(invoiceData);
    } finally {
      setIsAddingInvoice(false);
    }
  }

  const createInvoiceManually = useEditCreateInvoice(
    () => {
      toaster.create({
        title: "Recaudo creado",
        description: "El recaudo se ha creado con éxito.",
        type: "success",
        duration: 5000,
        meta: { closable: true },
      });
      refetch();
    },
    () => {
      toaster.create({
        title: "Error",
        description: "Hubo un error al crear el recaudo.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      });
    }
  );

  /////////////////////////////// FILE UPLOAD ///////////////////////////////////////

  const uploadFile = async (file: File): Promise<string> => {
    if (!merchant?.id) throw new Error("Merchant ID missing");

    const { uploadUrl, reference } = await getUploadUrl.mutateAsync({
      rentalsMerchantId: merchant.id,
    });

    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed (${response.status}): ${errorText}`);
    }

    return reference;
  };

  return (
    <>
      <Box display={"flex"} gap={"1.5rem"}>
        <Button
          size="sm"
          variant={"outline"}
          hidden={invoiceBatch?.status !== "processing" || !invoiceBatch}
          onClick={() => setModal("upload")}
        >
          Ver estado de la carga
        </Button>
        <Button
          size="sm"
          variant={"outline"}
          onClick={() => setModal("upload")}
          hidden={invoiceBatch?.status === "processing"}
        >
          Carga masiva de recaudos
        </Button>
        <Button
          size="sm"
          onClick={() => setModal("add")}
          loading={isAddingInvoice}
          loadingText="Agregando recaudo..."
        >
          <Plus />
          Agregar recaudo
        </Button>
      </Box>

      <AddInvoiceModal
        open={modal === "add"}
        onClose={() => setModal(null)}
        onSubmit={handleCreateInvoice}
      />

      <UploadFileModal
        isOpen={modal === "upload"}
        onClose={() => setModal(null)}
        invoiceBatch={invoiceBatch}
        uploadFile={uploadFile}
        resetBatch={resetBatch}
      />
    </>
  );
};

export default Buttons;
